import { MatrixClient } from 'matrix-js-sdk/src/matrix';

import logger from 'utils/logging/faro';
import { fetchMediaAsBlobUrl, uploadMediaToMatrix } from 'utils/matrix/matrixMediaUtils';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

// Default dimensions for custom background image thumbnails (Full HD)
export const DEFAULT_BACKGROUND_IMAGE_WIDTH = 1920;
export const DEFAULT_BACKGROUND_IMAGE_HEIGHT = 1080;

// Maximum file size for custom background image uploads (10MB)
export const MAX_CUSTOM_BACKGROUND_FILE_SIZE = 10 * 1024 * 1024; // 10MB (10,485,760 bytes)

export interface ImageDimensions {
  width: number;
  height: number;
  pixelCount: number;
}

export interface MatrixCustomImage {
  id: string;
  mxcUri: string; // mxc:// URI from Matrix
  name: string; // Original filename
  uploadedAt: number; // Timestamp
  hash?: string; // SHA-256 hash for duplicate detection
  dimensions?: ImageDimensions;
}

export interface CustomBackgroundImagesAccountData {
  images: MatrixCustomImage[];
}

/**
 * Extract error message from unknown error type
 * @param error - Unknown error value
 * @returns Error message string
 */
function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

/**
 * Upload an image file to Matrix media repository
 * @param matrixClient - Matrix client instance
 * @param file - Image file to upload
 * @returns Promise resolving to mxc:// URI
 */
export async function uploadImageToMatrix(matrixClient: MatrixClient, file: File): Promise<string> {
  // Use shared utility for uploading media to Matrix
  return uploadMediaToMatrix(matrixClient, file, { type: file.type });
}

/**
 * Get existing custom background images from Matrix account data
 * @param matrixClient - Matrix client instance
 * @returns Array of custom images
 */
export function getStoredImagesFromMatrix(matrixClient: MatrixClient | null): MatrixCustomImage[] {
  if (!matrixClient) return [];

  try {
    const accountData = matrixClient.getAccountData(CustomMeedioMatrixEvents.CUSTOM_BACKGROUND_IMAGES as never);
    if (!accountData) return [];

    const content = accountData.getContent<CustomBackgroundImagesAccountData>();
    return content?.images || [];
  } catch (error) {
    logger.warn('Error reading custom background images from Matrix account data', error);
    return [];
  }
}

/**
 * Store image reference in Matrix account data
 * @param matrixClient - Matrix client instance
 * @param image - Image data to store
 * @returns Promise that resolves when account data is updated
 */
export async function storeImageReferenceInMatrix(matrixClient: MatrixClient, image: MatrixCustomImage): Promise<void> {
  try {
    // Get existing images
    const existingImages = getStoredImagesFromMatrix(matrixClient);

    // Add new image
    const updatedImages = [...existingImages, image];

    // Store in account data
    await matrixClient.setAccountData(
      CustomMeedioMatrixEvents.CUSTOM_BACKGROUND_IMAGES as never,
      {
        images: updatedImages,
      } as never
    );
  } catch (error) {
    throw new Error(`Failed to store image reference in Matrix: ${getErrorMessage(error)}`);
  }
}

/**
 * Remove image reference from Matrix account data
 * Note: The actual media file remains in Matrix storage and will be cleaned up by the server
 * @param matrixClient - Matrix client instance
 * @param imageId - ID of image to remove
 * @returns Promise that resolves when account data is updated
 */
export async function removeImageReferenceFromMatrix(matrixClient: MatrixClient, imageId: string): Promise<void> {
  try {
    // Get existing images
    const existingImages = getStoredImagesFromMatrix(matrixClient);

    // Remove image by ID
    const updatedImages = existingImages.filter((img) => img.id !== imageId);

    // Store updated list in account data
    await matrixClient.setAccountData(
      CustomMeedioMatrixEvents.CUSTOM_BACKGROUND_IMAGES as never,
      {
        images: updatedImages,
      } as never
    );
  } catch (error) {
    throw new Error(`Failed to remove image reference from Matrix: ${getErrorMessage(error)}`);
  }
}

/**
 * Fetch image from Matrix and create a blob URL for display
 * This avoids ORB (Opaque Response Blocking) issues by fetching with authentication
 * @param matrixClient - Matrix client instance
 * @param mxcUri - mxc:// URI
 * @param width - Optional width for thumbnail (defaults to DEFAULT_BACKGROUND_IMAGE_WIDTH)
 * @param height - Optional height for thumbnail (defaults to DEFAULT_BACKGROUND_IMAGE_HEIGHT)
 * @returns Blob URL or null if fetch fails
 */
export async function getImageBlobUrlFromMatrix(
  matrixClient: MatrixClient | null,
  mxcUri: string,
  width: number = DEFAULT_BACKGROUND_IMAGE_WIDTH,
  height: number = DEFAULT_BACKGROUND_IMAGE_HEIGHT
): Promise<string | null> {
  // Use shared utility for fetching media as blob URL
  return fetchMediaAsBlobUrl(matrixClient, mxcUri, { width, height, method: 'scale' });
}

/**
 * Upload image and store reference in one operation
 * @param matrixClient - Matrix client instance
 * @param file - Image file to upload
 * @param hash - Optional SHA-256 hash for duplicate detection
 * @param dimensions - Optional image dimensions
 * @returns Promise resolving to the stored image data
 */
export async function uploadAndStoreImageInMatrix(
  matrixClient: MatrixClient,
  file: File,
  hash?: string,
  dimensions?: ImageDimensions
): Promise<MatrixCustomImage> {
  // Upload image to Matrix
  const mxcUri = await uploadImageToMatrix(matrixClient, file);

  // Create image record
  const image: MatrixCustomImage = {
    id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: file.name,
    uploadedAt: Date.now(),
    mxcUri,
    hash,
    dimensions,
  };

  // Store reference in account data
  await storeImageReferenceInMatrix(matrixClient, image);

  return image;
}
